import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import TextLink from 'components/Luxkit/TextLink'
import Caption from 'components/Luxkit/Typography/Caption'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { isLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { rem } from 'polished'
import { initialiseBnblOfferModal } from 'actions/BnblModalActions'
import { accountAccessShowLogin } from 'actions/UiActions'
import useReduxStateListener from 'hooks/useReduxStateListener'
import { OfferPackageOptionView, getBnblPackageViews } from 'selectors/offerPageSelectors'

import { useAppDispatch } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import BnblModal from './BnblModal/BnblModal'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import LineGiftIcon from 'components/Luxkit/Icons/line/LineGiftIcon'
import getDatasetFromEvent from 'lib/getDatasetFromEvent'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import Group from 'components/utils/Group'
import { useIntlDateFormatter } from 'lib/dateUtils'

const BNBLBox = styled.div`
  background-color: ${props => props.theme.palette.neutral.seven};
  padding: ${rem(8)} ${rem(16)};
  border-radius: ${props => props.theme.borderRadius.medium};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

interface Props {
  offer: App.Offer;
  isLoggedIn: boolean;
  baseBnblPackageViews: OfferPackageOptionView[];
  packageView: OfferPackageOptionView;
  rooms: Array<App.Occupants>;
}

function OfferPageBNBLcta(props: Props) {
  const dispatch = useAppDispatch()
  const intlFormatter = useIntlDateFormatter()
  const [bnblModalOpen, _, openBnblModal, closeBnblModal] = useToggle()
  const { offer, isLoggedIn, baseBnblPackageViews, packageView, rooms } = props

  const {
    package: pkg,
  } = packageView

  const intlDateFormatter = useIntlDateFormatter()

  const isSevenDaysBeforeTravelToDate = moment().add(1, 'w').isBefore(offer.travelToDate)

  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const loginListener = useReduxStateListener<{ loggedIn: boolean }>((state) => {
    if (isLoggedIn) {
      return { loggedIn: true }
    } else if (!state.ui.modal.open) {
      return { loggedIn: false }
    }
  })

  const openAndInitaliseBnblModal = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    openBnblModal()
    const isGift = getDatasetFromEvent(e, 'bnbltype') === 'gift'
    const numberOfRooms = rooms?.length ?? 1
    const bnblPackageView = baseBnblPackageViews.find(baseBnblPackageView => baseBnblPackageView.package.uniqueKey === pkg.uniqueKey)
    const packages = [{ packageView: bnblPackageView, numberOfRooms }]
    dispatch(initialiseBnblOfferModal(packages, offer.id, isGift))
  }, [baseBnblPackageViews, dispatch, offer.id, openBnblModal, pkg.uniqueKey, rooms?.length])

  const handleNewOrOldCheckout = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    openAndInitaliseBnblModal(e)
  }, [openAndInitaliseBnblModal])

  const onBuyNowBookLaterClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    trackEvent('BUY_NOW_CHOOSE_DATES_LATER_PRESSED')
    if (!isLoggedIn) {
      dispatch(accountAccessShowLogin())
      loginListener.awaitResult().then(({ loggedIn }) => {
        if (!loggedIn) return
        return handleNewOrOldCheckout(e)
      })
    } else {
      return handleNewOrOldCheckout(e)
    }
  }, [trackEvent, dispatch, handleNewOrOldCheckout, isLoggedIn, loginListener])

  return (<>
    <BNBLBox>
      <Group direction="horizontal" gap={8} wrap="wrap" horizontalAlign="start">
        <Caption variant="medium" weight="bold">
          Not ready to book just yet?
        </Caption>
        <Caption variant="medium">
          You can purchase or gift this package and choose dates when ready. Travel until {intlFormatter(offer.travelToDate, INTL_DMY_CASUAL_FORMAT)}
        </Caption>
        <Group direction="horizontal" gap={8} wrap="wrap">
          <TextLink
            size="small"
            onClick={onBuyNowBookLaterClick}
            data-testid="buyNowChooseDatesLater"
            startIcon={<LineCalendarIcon />}
          >
            Buy now, choose dates later
          </TextLink>

          {isSevenDaysBeforeTravelToDate &&
            <TextLink
              data-bookingtype="gift"
              size="small"
              onClick={onBuyNowBookLaterClick}
              data-testid="sendAsGift"
              data-bnbltype="gift"
              startIcon={<LineGiftIcon />}
            >
              Give as gift
            </TextLink>
          }
        </Group>
        {!isSevenDaysBeforeTravelToDate &&
          <Caption variant="medium">
            Travel until {intlDateFormatter(offer.travelToDate, INTL_DMY_CASUAL_FORMAT)}
          </Caption>
        }
      </Group>
    </BNBLBox>
    {bnblModalOpen && <BnblModal closeBnblModal={closeBnblModal} isOpen={bnblModalOpen} offer={offer}/>}
  </>)
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  offer: state.offer,
  isLoggedIn: isLoggedInSelector(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
}))(OfferPageBNBLcta)
